import data from "@emoji-mart/data";
import Picker from "@emoji-mart/react";
import { Music, Plus, Upload, X } from "lucide-react";
import React, { useState } from "react";
import { usePlaylistManagement } from "../hooks/usePlaylistManagement";
import { usePlaylistStore } from "../stores/playlistStore";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";

interface CreatePlaylistDialogProps {
  isOpen: boolean;
  onClose: () => void;
  provider: "local" | "spotify";
}

export const CreatePlaylistDialog: React.FC<CreatePlaylistDialogProps> = ({ isOpen, onClose, provider }) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const { createNewPlaylist: createSpotifyPlaylist } = usePlaylistManagement();
  const { createPlaylist: createLocalPlaylist } = usePlaylistStore();

  const handleEmojiSelect = (emoji: { native: string }) => {
    setName((prev) => prev + emoji.native);
    setShowEmojiPicker(false);
  };

  const handleAddTag = () => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      setTags([...tags, newTag.trim()]);
      setNewTag("");
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  const handleCoverSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setCoverFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setCoverPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    try {
      if (provider === "spotify") {
        await createSpotifyPlaylist.mutateAsync({ name, description });
      } else {
        createLocalPlaylist(name, description, coverPreview || undefined, tags, "local");
      }
      onClose();
      setName("");
      setDescription("");
      setTags([]);
      setCoverFile(null);
      setCoverPreview(null);
    } catch (error) {
      console.error("Failed to create playlist:", error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800">
        <DialogHeader>
          <DialogTitle>Create {provider === "local" ? "Local" : "Spotify"} Playlist</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-4">
            <div className="relative group">
              {coverPreview ? (
                <img src={coverPreview} alt="Playlist cover" className="w-full aspect-square object-cover rounded-lg" />
              ) : (
                <div className="w-full aspect-square bg-zinc-800 rounded-lg flex items-center justify-center">
                  <Music className="w-24 h-24 text-zinc-600" />
                </div>
              )}
              <Label
                htmlFor="cover-upload"
                className="absolute inset-0 flex items-center justify-center bg-black/60 opacity-0 group-hover:opacity-100 transition cursor-pointer rounded-lg"
              >
                <Upload className="w-12 h-12" />
                <Input id="cover-upload" type="file" accept="image/*" onChange={handleCoverSelect} className="hidden" />
              </Label>
            </div>

            <div>
              <Label htmlFor="name">Name</Label>
              <div className="relative">
                <Input
                  type="text"
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="bg-zinc-800"
                  placeholder="My Playlist"
                  required
                />
                <Button
                  type="button"
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white p-2"
                  variant="ghost"
                >
                  😊
                </Button>
              </div>
              {showEmojiPicker && (
                <div className="absolute z-50 mt-2">
                  <div onClick={(e) => e.stopPropagation()}>
                    <Picker
                      data={data}
                      onEmojiSelect={handleEmojiSelect}
                      theme="dark"
                      previewPosition="none"
                      skinTonePosition="none"
                    />
                  </div>
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="description">Description (optional)</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="bg-zinc-800"
                placeholder="Add an optional description"
                rows={3}
              />
            </div>

            <div>
              <Label htmlFor="tags">Tags</Label>
              <div className="flex flex-wrap gap-2 mb-2">
                {tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-zinc-800 rounded-full flex items-center gap-2 group">
                    {tag}
                    <Button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="opacity-0 group-hover:opacity-100 transition"
                      variant="ghost"
                      size="icon"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  type="text"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleAddTag()}
                  placeholder="Add a tag"
                  className="bg-zinc-800"
                />
                <Button type="button" onClick={handleAddTag} variant="secondary">
                  <Plus className="w-4 h-4 mr-2" />
                  Add
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" onClick={onClose} variant="outline">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!name.trim() || (provider === "spotify" && createSpotifyPlaylist.isPending)}
              className="bg-yellow-500 text-white hover:bg-yellow-600"
            >
              {provider === "spotify" && createSpotifyPlaylist.isPending ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
